import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { IconSize, IconColor, sizeClasses, sizePx, getColorClass } from './icon-types';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export interface BaseIconProps {
  size?: IconSize;
  color?: IconColor;
  className?: string;
  'aria-hidden'?: boolean;
  /**
   * Explicit pixel size, overriding the `size` scale.
   *
   * For the rare glyph measured against a container rather than the UI icon
   * scale. The scale steps 20 → 24 → 48, so anything sized to sit inside the
   * 44px avatar circle has nothing to ask for: 24px reads small beside a sender
   * logo, and 48px does not fit at all.
   *
   * Prefer `size` everywhere else — this exists so one badge does not force a
   * new token into a scale every icon in the app shares.
   */
  sizePx?: number;
  /**
   * Opacity of the duotone glyph's secondary layer, defaulting to the registry's
   * 0.4 two-tone look.
   *
   * Set to 1 when the glyph is knocked out of a filled shape: a half-transparent
   * layer there does not read as a second tone, it reads as a mark that failed
   * to draw. Mirrors `createDuotoneIcon`'s option of the same name, moved to the
   * call site because the same registered icon is wanted both ways.
   */
  secondaryOpacity?: number;
}

interface CreateIconOptions {
  defaultSize?: IconSize;
  flip?: 'horizontal' | 'vertical' | 'both';
}

/** Create a generic icon wrapper component for FontAwesome. */
export const createIcon = (
  iconDefinition: IconDefinition,
  options: IconSize | CreateIconOptions = 'md'
) => {
  const { defaultSize = 'md', flip } =
    typeof options === 'string' ? { defaultSize: options, flip: undefined } : options;

  return React.forwardRef<SVGSVGElement, BaseIconProps>(
    (
      {
        size = defaultSize,
        color = 'primary',
        className = '',
        sizePx: pxOverride,
        secondaryOpacity = 0.4,
        ...props
      },
      ref
    ) => {
      const colorClass = getColorClass(color);
      // The scale's h/w class is dropped when an explicit size is given, so the
      // class and the inline style cannot disagree about the same box.
      const sizeClass = pxOverride === undefined ? sizeClasses[size] : '';
      const px = pxOverride ?? sizePx[size];
      const combinedClassName = `${sizeClass} ${colorClass} ${className}`.replace(/\s+/g, ' ').trim();

      return (
        <FontAwesomeIcon
          ref={ref}
          icon={iconDefinition}
          className={combinedClassName}
          // Registry glyphs are duotone: dim the secondary layer for the two-tone
          // look, unless the call site is knocking the glyph out of a filled shape.
          style={{ width: px, height: px, '--fa-secondary-opacity': String(secondaryOpacity) }}
          aria-hidden={props['aria-hidden'] ?? true}
          {...(flip ? { flip } : {})}
          {...props}
        />
      );
    }
  );
};

interface CreateDuotoneIconOptions {
  defaultSize?: IconSize;
  flip?: 'horizontal' | 'vertical' | 'both';
  /** Opacity of the lighter secondary layer (0–1). */
  secondaryOpacity?: number;
}

/**
 * Like {@link createIcon} but renders a duotone glyph as a two-tone of the
 * current text color: the primary layer at full strength, the secondary at
 * `secondaryOpacity`. Color is NOT forced — pass it via a `text-*` className (or
 * the `color` prop) and both layers inherit it, so semantic color classes
 * (success green, destructive red, amber stars, …) just work.
 */
export const createDuotoneIcon = (
  iconDefinition: IconDefinition,
  options: IconSize | CreateDuotoneIconOptions = 'md'
) => {
  const {
    defaultSize = 'md',
    flip,
    secondaryOpacity = 0.4,
  } = typeof options === 'string' ? { defaultSize: options, flip: undefined } : options;

  return React.forwardRef<SVGSVGElement, BaseIconProps>(
    ({ size = defaultSize, color, className = '', ...props }, ref) => {
      const colorClass = color ? getColorClass(color) : '';
      const px = sizePx[size];
      const combinedClassName = `${sizeClasses[size]} ${colorClass} ${className}`
        .replace(/\s+/g, ' ')
        .trim();

      return (
        <FontAwesomeIcon
          ref={ref}
          icon={iconDefinition}
          className={combinedClassName}
          style={{ width: px, height: px, '--fa-secondary-opacity': String(secondaryOpacity) }}
          aria-hidden={props['aria-hidden'] ?? true}
          {...(flip ? { flip } : {})}
          {...props}
        />
      );
    }
  );
};
